import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ChevronDown, Menu, X } from "lucide-react";

const NAV = [
  {
    label: "Services",
    children: [
      { label: "Meta Advertising", to: "/services/meta-advertising" },
      { label: "Social Media Management", to: "/services/social-media-management" },
      { label: "Content Creation", to: "/services/content-creation" },
      { label: "Google Advertising", to: "/services/google-ads" },
      { label: "Website Design", to: "/services/website-design" },
      { label: "Branding & Design", to: "/services/branding" },
      { label: "Email Marketing", to: "/services/email-marketing" },
      { label: "SEO", to: "/services/seo" },
      { label: "Google Business Profile", to: "/services/google-business-profile" },
    ],
  },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Team", to: "/team" },
  { label: "About", to: "/about" },
  { label: "Resources", to: "/resources" },
];

export default function Header() {
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpen(null);
  }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur border-b border-[var(--ob-border)]" : "bg-white"
      }`}
    >
      <div className="ob-container flex items-center justify-between h-20">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-2.5">
          <img
            src="/brand/main-logo.png"
            alt="Onboard Marketing"
            width="1247"
            height="126"
            className="h-6 md:h-7 w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <div
              key={item.label}
              className="ob-nav-item"
              onMouseEnter={() => item.children && setOpen(item.label)}
              onMouseLeave={() => setOpen(null)}
            >
              {item.to ? (
                <NavLink
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `ob-nav-link ${isActive ? "opacity-100" : ""}`
                  }
                >
                  {item.label}
                  {item.children && <ChevronDown size={14} strokeWidth={1.5} />}
                </NavLink>
              ) : (
                <button
                  type="button"
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="ob-nav-link"
                  onClick={() => setOpen(open === item.label ? null : item.label)}
                >
                  {item.label}
                  {item.children && <ChevronDown size={14} strokeWidth={1.5} />}
                </button>
              )}

              {item.children && open === item.label && (
                <div className="absolute top-full left-0 pt-2 min-w-[280px]">
                  <div className="bg-white border border-[var(--ob-border)] rounded-2xl shadow-sm py-2">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        data-testid={`nav-child-${c.to.split("/").pop()}`}
                        className="block px-5 py-2.5 text-[14px] hover:bg-[var(--ob-bg-alt)] transition-colors"
                        style={{ color: "var(--ob-ink)" }}
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <Link to="/contact" className="ob-btn ob-btn-primary" data-testid="header-cta-btn">
            Let's have a chat
          </Link>
        </div>

        <button
          className="lg:hidden p-2 -mr-2"
          onClick={() => setMobile((v) => !v)}
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
        >
          {mobile ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobile && (
        <div className="lg:hidden border-t border-[var(--ob-border)] bg-white" data-testid="mobile-menu">
          <div className="ob-container py-6 flex flex-col gap-1">
            {NAV.map((item) => (
              <div key={item.label} className="py-1">
                {item.to ? (
                  <Link
                    to={item.to}
                    className="block py-2 text-[17px]"
                    style={{ color: "var(--ob-ink)" }}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <>
                    <div
                      className="block py-2 text-[17px] opacity-70"
                      style={{ color: "var(--ob-ink)" }}
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {item.label}
                    </div>
                    {item.children && (
                      <div className="pl-4 mt-1 mb-2 flex flex-col gap-1 border-l border-[var(--ob-border)]">
                        {item.children.map((c) => (
                          <Link
                            key={c.to}
                            to={c.to}
                            className="block py-1.5 text-[15px]"
                            style={{ color: "var(--ob-ink)" }}
                            data-testid={`mobile-nav-child-${c.to.split("/").pop()}`}
                          >
                            {c.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              <Link to="/contact" className="ob-btn ob-btn-primary w-full" data-testid="mobile-cta-btn">
                Let's have a chat
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
